"use client";

import * as React from "react";
import Image from "next/image";
import type { CookieWall as CookieWallType } from "@/payload-types";
import { Button, ButtonVariant } from "@/components/frontend/ui/button";
import NextLink from "next/link";
import { Dialog, DialogHeader, DialogContent, DialogFooter, DialogCloseButton } from "@/components/frontend/ui/dialog";
import { pushConsentUpdate, GTM_CONSENT_STORAGE_KEY } from "@/lib/data-measurements/gtm-consent";
import { cn } from "@/lib/utils";
import { Cookie, Lock } from "lucide-react";
import { ToggleGroup, Toggle } from "@/components/frontend/ui/toggle-group";
import { Checkbox } from "@/components/frontend/ui/form/inputs/checkbox";
import { useTranslations } from "next-intl";

export interface CookieConsentState {
	necessary: boolean;
	preferences: boolean;
	analytics: boolean;
	marketing: boolean;
}

function getStoredConsent(): CookieConsentState | null {
	if (typeof window === "undefined") return null;
	try {
		const raw = window.localStorage.getItem(GTM_CONSENT_STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as CookieConsentState;
		return parsed && typeof parsed.necessary === "boolean" ? parsed : null;
	} catch {
		return null;
	}
}

function setStoredConsent(state: CookieConsentState): void {
	try {
		window.localStorage.setItem(GTM_CONSENT_STORAGE_KEY, JSON.stringify(state));
	} catch {}
}

interface CookieWallFloatingButtonProps {
	show: boolean;
	variant: ButtonVariant;
	alignment: "bottom-left" | "bottom-right";
	ariaLabel: string;
	onClick: () => void;
}

function CookieWallFloatingButton({ show, variant, alignment, ariaLabel, onClick }: CookieWallFloatingButtonProps) {
	if (!show) return null;
	return (
		<Button
			type="button"
			variant={variant}
			className={cn("fixed bottom-3 z-1000 flex items-center justify-center rounded-full shadow-lg", alignment === "bottom-right" ? "right-3" : "left-3")}
			onClick={onClick}
			aria-label={ariaLabel}
			title={ariaLabel}
		>
			<Cookie className="size-[1em]" aria-hidden />
		</Button>
	);
}

interface CookieWallProps {
	data: CookieWallType | null;
	faviconUrl?: string | null;
}

type TabId = "consent" | "details" | "about";

const BUTTON_ROW_CLASS = cn("flex shrink-0 flex-col gap-2 sm:flex-row sm:flex-wrap", "[&>button]:w-full sm:[&>button]:w-auto");

export function CookieWall({ data, faviconUrl }: CookieWallProps) {
	const [open, setOpen] = React.useState(false);
	const [hasExistingConsent, setHasExistingConsent] = React.useState(false);
	const [activeTab, setActiveTab] = React.useState<TabId>("consent");
	const [preferences, setPreferences] = React.useState(true);
	const [analytics, setAnalytics] = React.useState(true);
	const [marketing, setMarketing] = React.useState(true);

	React.useEffect(() => {
		if (!data?.active) return;
		const stored = getStoredConsent();
		setOpen(stored === null);
		if (stored === null) setHasExistingConsent(false);
		if (stored) {
			setPreferences(stored.preferences);
			setAnalytics(stored.analytics);
			setMarketing(stored.marketing);
		} else if (data?.content) {
			setPreferences(data.content.preferencesEnabled ?? true);
			setAnalytics(data.content.analyticsEnabled ?? true);
			setMarketing(data.content.marketingEnabled ?? true);
		}
	}, [data?.active, data?.content]);

	const t = useTranslations("cookiewall");
	const tabs = React.useMemo<{ id: TabId; label: string }[]>(
		() => [
			{ id: "consent", label: t("tabs.consent") },
			{ id: "details", label: t("tabs.details") },
			{ id: "about", label: t("tabs.about") },
		],
		[t],
	);

	const handleClose = React.useCallback((next: CookieConsentState) => {
		setStoredConsent(next);
		pushConsentUpdate(next);
		setOpen(false);
	}, []);

	const handleAccept = React.useCallback(() => {
		setPreferences(true);
		setAnalytics(true);
		setMarketing(true);
		handleClose({
			necessary: true,
			preferences: true,
			analytics: true,
			marketing: true,
		});
	}, [handleClose]);

	const handleSave = React.useCallback(() => {
		handleClose({
			necessary: true,
			preferences,
			analytics,
			marketing,
		});
	}, [handleClose, preferences, analytics, marketing]);

	const handleReject = React.useCallback(() => {
		setPreferences(false);
		setAnalytics(false);
		setMarketing(false);
		handleClose({
			necessary: true,
			preferences: false,
			analytics: false,
			marketing: false,
		});
	}, [handleClose]);

	const handleTabChange = React.useCallback((tab: string[]) => {
		if (tab.length === 0) return;
		setActiveTab(tab[0] as TabId);
	}, []);

	const handleOpenChange = React.useCallback(
		(next: boolean) => {
			if (next) setOpen(true);
			else if (hasExistingConsent) setOpen(false);
		},
		[hasExistingConsent],
	);

	const openDetails = React.useCallback(() => {
		const stored = getStoredConsent();
		if (stored) {
			setPreferences(stored.preferences);
			setAnalytics(stored.analytics);
			setMarketing(stored.marketing);
		}
		setHasExistingConsent(true);
		setActiveTab("details");
		setOpen(true);
	}, []);

	if (!data?.active) return null;

	const content = data.content;

	return (
		<>
			<Dialog
				trigger={
					<CookieWallFloatingButton
						show={data.showFloatingButton !== false}
						variant={data.floatingButtonVariant ?? "default"}
						alignment={data.floatingButtonAlignment ?? "bottom-left"}
						ariaLabel={t("a11y.floating-button-aria-label")}
						onClick={openDetails}
					/>
				}
				open={open}
				onOpenChange={handleOpenChange}
				variant={data.cardVariant}
				title={data.consentTitle?.trim() || t("consent.title")}
			>
				<DialogHeader>
					<div className="flex shrink-0 items-center justify-between gap-3">
						{faviconUrl && (
							<Image
								src={faviconUrl}
								alt={t("a11y.favicon-alt")}
								className="h-12 w-12 shrink-0 rounded object-contain"
								width={48}
								height={48}
								unoptimized={faviconUrl.startsWith("/") || faviconUrl.startsWith("http")}
							/>
						)}
						{hasExistingConsent && <DialogCloseButton variant={data.closeButtonVariant ?? "default"} label={t("a11y.close-label")} />}
					</div>
				</DialogHeader>
				<DialogContent>
					<div className="flex min-h-0 max-h-[50vh] sm:max-h-none flex-col">
						<ToggleGroup
							variant={data.toggleGroupVariant}
							multiple={false}
							value={[activeTab]}
							onValueChange={handleTabChange}
							className="mb-4 shrink-0"
						>
							{tabs.map((tab) => (
								<Toggle
									key={tab.id}
									id={`ms-cookiewall-tab-${tab.id}`}
									aria-controls={`ms-cookiewall-panel-${tab.id}`}
									value={tab.id}
									className="grow"
								>
									{tab.label}
								</Toggle>
							))}
						</ToggleGroup>
						<div
							id="ms-cookiewall-panel-consent"
							role="tabpanel"
							aria-labelledby="ms-cookiewall-tab-consent"
							className="flex min-h-0 flex-1 flex-col"
							hidden={activeTab !== "consent"}
						>
							<div className="min-h-0 flex-1 overflow-auto text-sm">
								<strong className="mb-1 block font-semibold">{data.consentTitle?.trim() || t("consent.title")}</strong>
								{data.consentText?.trim() || t("consent.text")}
								{data.policyUrl?.trim() ? (
									<>
										{" "}
										<NextLink
											href={data.policyUrl?.trim() ?? ""}
											className="text-accent underline hover:opacity-90"
											target="_blank"
											rel="noopener noreferrer"
										>
											{data.learnMoreLabel?.trim() || t("consent.learn-more")}
										</NextLink>
									</>
								) : null}
							</div>
						</div>
						<div
							id="ms-cookiewall-panel-details"
							role="tabpanel"
							aria-labelledby="ms-cookiewall-tab-details"
							className="flex min-h-0 flex-1 flex-col"
							hidden={activeTab !== "details"}
						>
							<div className="min-h-0 flex-1 overflow-auto">
								<div className="space-y-4">
									{content?.necessaryEnabled !== false && (
										<div data-category="necessary" className="flex flex-col gap-1 cursor-not-allowed opacity-80 **:cursor-not-allowed">
											<Checkbox
												checked
												disabled
												aria-describedby="cw-necessary-desc"
												label={
													<span className="flex items-center gap-1.5 text-sm font-medium">
														{content?.necessaryLabel?.trim() || t("details.necessary-label")}
														<Lock className="size-3.5 shrink-0 opacity-70" aria-hidden />
													</span>
												}
											/>
											{(content?.necessaryDescription?.trim() || t("details.necessary-description")) && (
												<span id="cw-necessary-desc" className="text-xs md:text-sm pl-7">
													{content?.necessaryDescription?.trim() || t("details.necessary-description")}
												</span>
											)}
										</div>
									)}
									{content?.preferencesEnabled !== false && (
										<div className="flex flex-col gap-1">
											<Checkbox
												id="cw-preferences"
												checked={preferences}
												onCheckedChange={(checked) => setPreferences(checked === true)}
												aria-describedby="cw-preferences-desc"
												label={
													<span className="text-sm font-medium">
														{content?.preferencesLabel?.trim() || t("details.preferences-label")}
													</span>
												}
											/>
											{(content?.preferencesDescription?.trim() || t("details.preferences-description")) && (
												<span id="cw-preferences-desc" className="text-xs md:text-sm pl-7">
													{content?.preferencesDescription?.trim() || t("details.preferences-description")}
												</span>
											)}
										</div>
									)}
									{content?.analyticsEnabled !== false && (
										<div className="flex flex-col gap-1">
											<Checkbox
												id="cw-analytics"
												checked={analytics}
												onCheckedChange={(checked) => setAnalytics(checked === true)}
												aria-describedby="cw-analytics-desc"
												label={
													<span className="text-sm font-medium">
														{content?.analyticsLabel?.trim() || t("details.analytics-label")}
													</span>
												}
											/>
											{(content?.analyticsDescription?.trim() || t("details.analytics-description")) && (
												<span id="cw-analytics-desc" className="text-xs md:text-sm pl-7">
													{content?.analyticsDescription?.trim() || t("details.analytics-description")}
												</span>
											)}
										</div>
									)}
									{content?.marketingEnabled !== false && (
										<div className="flex flex-col gap-1">
											<Checkbox
												id="cw-marketing"
												checked={marketing}
												onCheckedChange={(checked) => setMarketing(checked === true)}
												aria-describedby="cw-marketing-desc"
												label={
													<span className="text-sm font-medium">
														{content?.marketingLabel?.trim() || t("details.marketing-label")}
													</span>
												}
											/>
											{(content?.marketingDescription?.trim() || t("details.marketing-description")) && (
												<span id="cw-marketing-desc" className="text-xs md:text-sm pl-7">
													{content?.marketingDescription?.trim() || t("details.marketing-description")}
												</span>
											)}
										</div>
									)}
								</div>
							</div>
						</div>
						{(data.about?.trim() || t("about")) && (
							<div
								id="ms-cookiewall-panel-about"
								role="tabpanel"
								aria-labelledby="ms-cookiewall-tab-about"
								className="flex min-h-0 flex-1 flex-col"
								hidden={activeTab !== "about"}
							>
								<div className="min-h-0 flex-1 overflow-auto">
									<div className="whitespace-pre-line text-sm">{data.about?.trim() || t("about")}</div>
								</div>
							</div>
						)}
					</div>
				</DialogContent>

				<DialogFooter>
					<div className={BUTTON_ROW_CLASS}>
						{activeTab === "details" ? (
							<>
								<Button type="button" variant={data.acceptButtonVariant ?? "default"} onClick={handleAccept}>
									{data.acceptLabel?.trim() || t("buttons.accept-all")}
								</Button>
								<Button type="button" variant={data.saveButtonVariant ?? "subtle"} onClick={handleSave}>
									{data.saveLabel?.trim() || t("buttons.save-preferences")}
								</Button>
								{data.showRejectButton !== false && (
									<Button type="button" variant={data.rejectButtonVariant ?? "destructive"} onClick={handleReject}>
										{data.rejectLabel?.trim() || t("buttons.reject-non-necessary")}
									</Button>
								)}
							</>
						) : (
							<>
								<Button type="button" variant={data.acceptButtonVariant ?? "default"} onClick={handleAccept}>
									{data.acceptLabel?.trim() || t("buttons.accept-all")}
								</Button>
								<Button type="button" variant={data.manageButtonVariant ?? "subtle"} onClick={() => handleTabChange(["details"])}>
									{data.manageLabel?.trim() || t("buttons.customize")}
								</Button>
								{data.showRejectButton !== false && (
									<Button type="button" variant={data.rejectButtonVariant ?? "destructive"} onClick={handleReject}>
										{data.rejectLabel?.trim() || t("buttons.reject-non-necessary")}
									</Button>
								)}
							</>
						)}
					</div>
				</DialogFooter>
			</Dialog>
		</>
	);
}
